import { normalize } from 'normalizr'
import { Schemas } from 'redux-modules/schema'
import { updateEntities, deleteEntity } from 'redux-modules/entity'

import api from 'api'

const FETCH_REQUEST = 'realhub/providerDisclaimer/FETCH_REQUEST'
const FETCH_SUCCESS = 'realhub/providerDisclaimer/FETCH_SUCCESS'
const FETCH_FAILURE = 'realhub/providerDisclaimer/FETCH_FAILURE'
const CREATE_REQUEST = 'realhub/providerDisclaimer/CREATE_REQUEST'
const CREATE_SUCCESS = 'realhub/providerDisclaimer/CREATE_SUCCESS'
const CREATE_FAILURE = 'realhub/providerDisclaimer/CREATE_FAILURE'
const UPDATE_REQUEST = 'realhub/providerDisclaimer/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realhub/providerDisclaimer/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realhub/providerDisclaimer/UPDATE_FAILURE'
const DELETE_REQUEST = 'realhub/providerDisclaimer/DELETE_REQUEST'
const DELETE_SUCCESS = 'realhub/providerDisclaimer/DELETE_SUCCESS'
const DELETE_FAILURE = 'realhub/providerDisclaimer/DELETE_FAILURE'

// Initial State
const initialState = {
  loaded: false,
  loadedForProviderIds: [],
  loading: false,
  creating: false,
  updating: false,
  deleting: false,
  errors: [],
}

// Actions
export function fetchRequest(payload = {}){
  return {
    type: FETCH_REQUEST,
    loadedForProviderIds: payload.loadedForProviderIds,
  }
}

export function fetchSuccess(){
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors = []){
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest(){
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess(payload = {}){
  return {
    type: CREATE_SUCCESS,
    result: payload.result,
  }
}

export function createFailure(errors = []){
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest(){
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(){
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors = []){
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest(){
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess(){
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors = []){
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Helper Functions
const handleError = errors => errors || ['Something went wrong']

// Action Creators
export function loadProviderDisclaimers(providerId, options){
  return (dispatch, getState) => {
    // Set Loading and add key to loaded array
    const providerDisclaimers = getState().providerDisclaimers
    const loadedForProviderIds = providerDisclaimers.loadedForProviderIds
      ? providerDisclaimers.loadedForProviderIds.slice()
      : []
    if (!loadedForProviderIds.includes(providerId)){
      loadedForProviderIds.push(providerId)
    }
    dispatch(fetchRequest({ loadedForProviderIds }))

    const promise = api(`/admin/providers/${providerId}/disclaimers.json`, options)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.PROVIDER_DISCLAIMER_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(fetchFailure(errors))
      })

    return promise
  }
}

export function createProviderDisclaimer(providerDisclaimer, options, callback){
  const config = {
    method: 'POST',
    body: JSON.stringify({
      provider_disclaimer: providerDisclaimer,
    }),
  }

  return (dispatch) => {
    // Set Loading
    dispatch(createRequest())

    const url = `/admin/providers/${providerDisclaimer.provider_id}/disclaimers.json`
    const promise = api(url, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.PROVIDER_DISCLAIMER)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        if (callback) callback(json)
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(createFailure(errors))
      })

    return promise
  }
}

export function updateProviderDisclaimer(providerDisclaimer, options, callback){
  const config = {
    method: 'PUT',
    body: JSON.stringify({
      provider_disclaimer: providerDisclaimer,
    }),
  }

  return (dispatch) => {
    // Set Loading
    dispatch(updateRequest())

    const { id, provider_id } = providerDisclaimer
    const url = `/admin/providers/${provider_id}/disclaimers/${id}.json`
    const promise = api(url, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.PROVIDER_DISCLAIMER)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        if (callback) callback(json)
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(updateFailure(errors))
      })

    return promise
  }
}

export function deleteProviderDisclaimer(providerDisclaimer, options, callback){
  const config = {
    method: 'DELETE',
  }

  return (dispatch) => {
    // Set Loading
    dispatch(deleteRequest())

    const { provider_id, id } = providerDisclaimer
    const url = `/admin/providers/${provider_id}/disclaimers/${id}.json`
    const promise = api(url, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.PROVIDER_DISCLAIMER)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        if (callback) callback(json)
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(deleteFailure(errors))
      })

    return promise
  }
}

// Reducer
export default function reducer(state = initialState, action = {}){
  switch (action.type){
    case FETCH_REQUEST:
      return Object.assign({}, state, {
        loading: true,
        loadedForProviderIds: action.loadedForProviderIds || state.loadedForProviderIds,
      })
    case FETCH_SUCCESS:
      return Object.assign({}, state, {
        loading: false,
        loaded: true,
        errors: [],
      })
    case FETCH_FAILURE:
      return Object.assign({}, state, {
        loading: false,
        loaded: false,
        errors: action.errors,
      })
    case CREATE_REQUEST:
      return Object.assign({}, state, {
        creating: true,
      })
    case CREATE_SUCCESS:
      return Object.assign({}, state, {
        creating: false,
        errors: [],
      })
    case CREATE_FAILURE:
      return Object.assign({}, state, {
        creating: false,
        errors: action.errors,
      })
    case UPDATE_REQUEST:
      return Object.assign({}, state, {
        updating: true,
      })
    case UPDATE_SUCCESS:
      return Object.assign({}, state, {
        updating: false,
        errors: [],
      })
    case UPDATE_FAILURE:
      return Object.assign({}, state, {
        updating: false,
        errors: action.errors,
      })
    case DELETE_REQUEST:
      return Object.assign({}, state, {
        deleting: true,
      })
    case DELETE_SUCCESS:
      return Object.assign({}, state, {
        deleting: false,
        errors: [],
      })
    case DELETE_FAILURE:
      return Object.assign({}, state, {
        deleting: false,
        errors: action.errors,
      })
    default:
      return state
  }
}
